/**
 * Safely print HTML content using a hidden iframe with srcdoc.
 * This approach:
 * - Avoids unsafe legacy DOM APIs (XSS risk) 
 * - Avoids Blob URLs (blocked by service workers/CSP on some deployments)
 * - Works cross-origin and in all browsers
 * - No popup blockers since it uses an iframe, not window.open()
 *
 * @param {string} htmlContent - Full HTML document string to render and print
 */
export const openPrintWindow = (htmlContent) => {
  // Remove any existing print iframe
  const existing = document.getElementById('__print_iframe');
  if (existing) existing.remove();

  const iframe = document.createElement('iframe');
  iframe.id = '__print_iframe';
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';

  // Strip any embedded print trigger scripts from the HTML 
  // (we handle print from the parent frame)
  const cleanHtml = htmlContent.replace(
    /<script[\s\S]*?<\/script>/gi,
    ''
  );

  iframe.srcdoc = cleanHtml;
  document.body.appendChild(iframe);

  iframe.onload = () => {
    // Wait for images to load inside the iframe
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    const imgs = iframeDoc.querySelectorAll('img');
    let loaded = 0;
    const total = imgs.length;

    const triggerPrint = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        // Fallback: open in new window if iframe print fails
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
      // Clean up iframe after a delay
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 60000);
    };

    if (total === 0) {
      setTimeout(triggerPrint, 300);
    } else {
      imgs.forEach((img) => {
        if (img.complete) {
          loaded++;
        } else {
          img.onload = () => { loaded++; if (loaded >= total) triggerPrint(); };
          img.onerror = () => { loaded++; if (loaded >= total) triggerPrint(); };
        }
      });
      if (loaded >= total) {
        setTimeout(triggerPrint, 300);
      }
      // Safety timeout in case images hang
      setTimeout(triggerPrint, 5000);
    }
  };
};
